/**
 * SonicfiAdoptCard -- "Config of record (preview)" on a SonicFi/CyberTan RSW switch sheet (OP#1838).
 *
 * WHY (owner decision 2026-10-13, OP#1820): these switches boot their factory uCentral document
 * at every start, so a VLAN set by hand is lost at the next reboot, and a first controller push
 * would replace the whole switch configuration. This card shows, READ-ONLY, the config of record
 * Meridian would build from what the switch runs:
 *   - the verdict (would the first push change forwarding? it must not);
 *   - the running trunk ports read from the switch;
 *   - "kept after a reboot": the ports whose VLANs a reboot drops today and the config of record
 *     makes durable;
 *   - every blocking item, each with why it matters;
 *   - the candidate document (collapsed).
 * There is NO push button: the first push is a separate owner-approved procedure (OP#1839) with a
 * bench switch first and someone on site. "Read the switch" is one audited, read-only SSH login
 * (site_operator+; RoleGate disables, never hides).
 */
import { useSonicfiAdoptPreview, useSonicfiAdoptRead } from "@/lib/api/sonicfiAdopt";
import { adoptVerdict, faceplate, runningTrunks, vlansText } from "@/lib/sonicfiAdopt";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import { ErrorBanner } from "@/components/ui/Page";
import { RoleGate } from "@/components/auth/RoleGate";

const READ_ROLE = "site_operator" as const;

export function SonicfiAdoptCard({ serial }: { serial: string }) {
  const preview = useSonicfiAdoptPreview(serial);
  const read = useSonicfiAdoptRead(serial);
  const p = read.data ?? preview.data;
  const v = adoptVerdict(p);
  const tone = v.verdict === "no_op" ? "healthy" : v.verdict === "changes" ? "critical" : "unknown";
  const trunks = runningTrunks(p);
  const blocking = (p?.gaps ?? []).filter((g) => g.severity === "blocking");
  const important = (p?.gaps ?? []).filter((g) => g.severity === "important");

  return (
    <div className="md-card p-4" data-testid="sonicfi-adopt-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-ink">Config of record (preview)</span>
        <StatusPill status={tone}>
          {v.verdict === "no_op" ? "No change to forwarding" : v.verdict === "changes" ? "Would change ports" : "Not read"}
        </StatusPill>
        <div className="ml-auto">
          <RoleGate min={READ_ROLE} mode="disable" reason="Reading the switch requires a site operator.">
            <Button variant="secondary" disabled={read.isPending} onClick={() => read.mutate()}>
              {read.isPending ? "Reading the switch…" : "Read the switch"}
            </Button>
          </RoleGate>
        </div>
      </div>

      <p className="mt-2 text-xs text-ink-muted">
        This switch has no configuration saved by Meridian. It loads its factory settings at every
        restart, so VLANs added by hand are lost. This preview shows the configuration Meridian would
        save, built from what the switch runs now. Nothing is sent to the switch.
      </p>

      {(preview.isError || read.isError) && (
        <div className="mt-2">
          <ErrorBanner error={read.error ?? preview.error} />
        </div>
      )}
      {p?.read_error && (
        <p className="mt-2 text-xs text-warn" data-testid="sonicfi-read-error">
          The switch was not read: {p.read_error.message}
        </p>
      )}

      <p className="mt-3 text-sm text-ink" data-testid="sonicfi-verdict">{v.text}</p>
      {p?.snapshot?.read_at && (
        <p className="text-xs text-ink-muted">
          Read {p.snapshot.read_at}
          {p.access?.mode === "relay" && p.access.gateway ? ` through gateway ${p.access.gateway}` : ""}.
        </p>
      )}

      {trunks.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-medium text-ink-muted">Ports with VLANs (read from the switch)</div>
          <ul className="mt-1 text-xs">
            {trunks.map((t) => (
              <li key={t.port}>
                {faceplate(t.port)} ({t.port}): {vlansText(t.vlans)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!!p?.boot_vs_candidate?.length && (
        <div className="mt-3">
          <div className="text-xs font-medium text-ink-muted">Kept after a restart once saved (lost today)</div>
          <ul className="mt-1 text-xs">
            {p.boot_vs_candidate.map((d) => (
              <li key={d.port}>
                {faceplate(d.port)}: at restart today {vlansText(d.before)}; saved {vlansText(d.after)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!!p?.running_vs_candidate?.length && (
        <div className="mt-3" data-testid="sonicfi-running-diff">
          <div className="text-xs font-medium text-critical">Ports the saved configuration would change</div>
          <ul className="mt-1 text-xs">
            {p.running_vs_candidate.map((d) => (
              <li key={d.port}>
                {faceplate(d.port)}: now {vlansText(d.before)}; would be {vlansText(d.after)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {blocking.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-medium text-ink-muted">Before the first save to the switch</div>
          <ul className="mt-1 flex flex-col gap-1 text-xs">
            {blocking.map((g) => (
              <li key={g.id} title={g.why}>
                <span className="font-medium">{g.question}</span>
                <span className="text-ink-muted"> {g.why}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {important.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1 text-xs text-ink-muted">
          {important.map((g) => (
            <li key={g.id} title={g.why}>{g.question}</li>
          ))}
        </ul>
      )}

      {p && !p.push_available && (
        <p className="mt-3 text-xs text-ink-muted">{p.push_unavailable_reason}</p>
      )}

      {p?.candidate && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs font-medium text-ink-muted">Configuration Meridian would save</summary>
          <pre className="mt-2 max-h-80 overflow-auto rounded bg-surface-2 p-2 text-[11px]">
            {JSON.stringify(p.candidate, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
